import type { AuthUser, OrgMembership } from "@/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  organizations: OrgMembership[];
  currentOrgId: string | null;
  currentOrgRole: string | null;

  setAuth: (
    token: string,
    user: AuthUser,
    organizations?: OrgMembership[],
  ) => void;
  hydrateSession: (payload: {
    token: string;
    user: AuthUser;
    organizations: OrgMembership[];
    currentOrg: { id: string; role: string } | null;
  }) => void;
  setOrg: (orgId: string, role: string, token: string) => void;
  setOrganizations: (orgs: OrgMembership[]) => void;
  clearAuth: () => void;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
  hasOrg: () => boolean;
  isOwnerOrAdmin: () => boolean;
  isPlatformAdmin: () => boolean;
  isCrmMember: () => boolean;
  isAdminUser: () => boolean;
  isTeamLeaderOnly: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      organizations: [],
      currentOrgId: null,
      currentOrgRole: null,

      setAuth: (token, user, organizations) =>
        set({
          token,
          user,
          currentOrgId: null,
          currentOrgRole: null,
          ...(organizations !== undefined ? { organizations } : {}),
        }),

      hydrateSession: ({ token, user, organizations, currentOrg }) =>
        set({
          token,
          user,
          organizations,
          currentOrgId: currentOrg?.id ?? null,
          currentOrgRole: currentOrg?.role ?? null,
        }),

      setOrg: (orgId, role, token) =>
        set({ currentOrgId: orgId, currentOrgRole: role, token }),

      setOrganizations: (orgs) => set({ organizations: orgs }),

      clearAuth: () =>
        set({
          token: null,
          user: null,
          organizations: [],
          currentOrgId: null,
          currentOrgRole: null,
        }),

      logout: async () => {
        const token = get().token;

        try {
          await fetch(
            `${import.meta.env.VITE_TRACKER_API_URL || "http://localhost:3020/api"}/auth/logout`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-Session-Client": "web",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            },
          );
        } catch {
          // Best-effort revoke; local state is cleared either way.
        }

        get().clearAuth();
      },

      isAuthenticated: () => !!get().token && !!get().user,
      hasOrg: () => !!get().currentOrgId,
      isOwnerOrAdmin: () => {
        const role = get().currentOrgRole;
        return role === "OWNER" || role === "ADMIN";
      },
      isPlatformAdmin: () => get().user?.platformRole === "PLATFORM_ADMIN",
      isCrmMember: () => get().user?.platformRole === "CRM_MEMBER",
      isAdminUser: () =>
        ["PLATFORM_ADMIN", "CRM_MEMBER"].includes(
          get().user?.platformRole ?? "",
        ),
      isTeamLeaderOnly: () => {
        const accessLevels = (get().user?.accessLevel ?? "")
          .split(",")
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);

        return (
          accessLevels.includes("team-leader") &&
          !accessLevels.some((value) =>
            ["super-admin", "admin", "hr", "manager"].includes(value),
          )
        );
      },
    }),
    {
      name: "tracker-auth-v3",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        organizations: state.organizations,
        currentOrgId: state.currentOrgId,
        currentOrgRole: state.currentOrgRole,
      }),
    },
  ),
);
